import slugify from 'slugify'; 
import { directusAPI } from '../../api'; // Use main directusAPI from api.js

// Generic function to handle errors from API requests
const handleApiError = (error) => {
  if (error.response) {
    console.error('Server responded with an error:', error.response.data);
    console.error('Status code:', error.response.status);
  } else if (error.request) {
    console.error('No response received:', error.request);
  } else {
    console.error('Error setting up request:', error.message);
  }
};

/**
 * Normalize fog_index to always be a number
 * Handles: "fog index: 15", "moderate", 12, etc.
 */
const normalizeFogIndex = (fogIndex) => {
  // If already a number, return it
  if (typeof fogIndex === 'number') {
    return Math.round(fogIndex);
  }
  
  // If null/undefined, return default
  if (!fogIndex) {
    return 12;
  }
  
  // Convert to string for processing
  const fogIndexStr = String(fogIndex).trim().toLowerCase();
  
  // Extract number from "fog index: 15" format
  const numberMatch = fogIndexStr.match(/(\d+)/);
  if (numberMatch) {
    return parseInt(numberMatch[1], 10);
  }
  
  // Map text descriptions to numbers
  const textToNumber = {
    'very simple': 8,
    'simple': 8,
    'easy': 8,
    'moderate': 12,
    'medium': 12,
    'average': 12,
    'complex': 16,
    'difficult': 16,
    'advanced': 16,
    'hard': 16
  };
  
  if (textToNumber[fogIndexStr]) {
    return textToNumber[fogIndexStr];
  }
  
  // Default fallback
  console.warn(`Could not parse fog index "${fogIndex}", using default: 12`);
  return 12;
};

// Removed processSources - no longer used

const saveArticleToDirectus = async (articleData, isFinal = false) => {
  try {
    
    const userId = localStorage.getItem('user_id');
    
    const dataToSave = {
      title: articleData.title || 'Untitled',
      slug: slugify(articleData.title || 'untitled', {
        lower: true,
        strict: true,
        remove: /[*+~.()'"!:@]/g
      }),
      status: isFinal ? 'published' : 'draft',
      fog_index: articleData.fog_index ? normalizeFogIndex(articleData.fog_index) : null,
      keywords: articleData.keywords || [],
      sources: articleData.sources ? JSON.stringify(articleData.sources) : null, // Keep for backward compatibility
      content_type: articleData.content_type || null,
      tone_of_voice: articleData.tone_of_voice || null,
      language: articleData.language || null,
      thread_id: articleData.thread_id || null,
      article_draft: articleData.article_draft || null,
      article_body: articleData.article_body ? JSON.stringify({
        content: articleData.article_body
      }) : null,
      article_context: articleData.article_context || null,
      project: articleData.project || null,
      article_thumbnail_included: articleData.article_thumbnail_included || false,
      article_thumbnail_source: articleData.article_thumbnail_source || null,
      featured_image: articleData.featured_image || null,
      article_linked_structure: articleData.article_linked_structure || null,
      // NOTE: article_linked_workspace is now assigned by backend (api-server.js / worker.js)
      // This ensures workspace is always correct even when frontend is bypassed
    };

    // Log article_thumbnail_source for debugging
    console.log('💾 SAVE_ARTICLE_THUMBNAIL_SOURCE:', {
      articleId: articleData.id,
      article_thumbnail_source: articleData.article_thumbnail_source,
      article_thumbnail_included: articleData.article_thumbnail_included,
      willSave: !!articleData.article_thumbnail_source
    });

    // Add user_created only for new articles (not updates)
    if (!articleData.id && userId) {
      dataToSave.user_created = userId;
    }

    // Make sure we preserve thread_id when updating an article
    if (articleData.id && !dataToSave.thread_id) {
      // Fetch current article data to preserve thread_id if not explicitly provided
      const currentArticle = await directusAPI.get(`/items/articles/${articleData.id}`);
      if (currentArticle.data && currentArticle.data.data && currentArticle.data.data.thread_id) {
        dataToSave.thread_id = currentArticle.data.data.thread_id;
      }
    }
    
    // Always ensure we keep the article_draft when saving an article_body
    if (articleData.id && articleData.article_body && !dataToSave.article_draft) {
      const currentArticle = await directusAPI.get(`/items/articles/${articleData.id}`);
      if (currentArticle.data && currentArticle.data.data && currentArticle.data.data.article_draft) {
        dataToSave.article_draft = currentArticle.data.data.article_draft;
      }
    }

    let response;
    if (articleData.id) {
      response = await directusAPI.patch(`/items/articles/${articleData.id}`, dataToSave);
    } else {
      response = await directusAPI.post('/items/articles', dataToSave);
    }
    
    // Log response to verify fields were saved
    console.log('💾 SAVE_RESPONSE:', {
      articleId: response.data.data.id,
      saved_article_thumbnail_source: response.data.data.article_thumbnail_source,
      saved_article_thumbnail_included: response.data.data.article_thumbnail_included,
      allKeys: Object.keys(response.data.data).filter(k => k.includes('thumbnail'))
    });

    // Handle M2M sources relationship for normal articles (not summaries)
    // DISABLED: M2M processing now handled directly in handleAddSource for immediate response

    return response.data;
  } catch (error) {
    if (error.response) {
      console.error('❌ DIRECTUS API ERROR:', error.response.status, error.response.data);
      
      // Handle 403 Forbidden - likely expired token
      if (error.response.status === 403) {
        console.error('❌ DIRECTUS 403 FORBIDDEN - Token likely expired');
        console.error('Current token:', localStorage.getItem('directus_token')?.substring(0, 20) + '...');
        
        // Clear expired token and redirect to login
        localStorage.removeItem('directus_token');
        localStorage.removeItem('user_id');
        sessionStorage.removeItem('directus_token');
        
        // Show user-friendly error
        alert('Your session has expired. Please log in again.');
        window.location.href = '/signin';
        return;
      }
    }
    throw error;
  }
};

// New function to update article status to 'ongoing'
const updateArticleStatusToOngoing = async (articleId) => {
  try {
    const response = await directusAPI.patch(`/items/articles/${articleId}`, {
      status: 'ongoing'
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Helper function to get workspace_id from localStorage or fetch from user
 * Can be used by other components that need workspace_id
 */
const getWorkspaceIdHelper = async () => {
  try {
    let workspaceId = localStorage.getItem('workspace_id');
    
    // If not in localStorage, fetch from user's linked_workspace
    if (!workspaceId) {
      const token = localStorage.getItem('directus_token');
      if (!token) {
        console.warn('WORKSPACE: No token available');
        return null;
      }
      
      const response = await directusAPI.get('/users/me', {
        params: { fields: ['linked_workspace.id'] }
      });
      
      const userData = response.data.data;
      if (userData?.linked_workspace?.id) {
        workspaceId = userData.linked_workspace.id;
        localStorage.setItem('workspace_id', workspaceId);
        console.log('WORKSPACE: Fetched and cached workspace_id:', workspaceId);
      } else {
        console.warn('WORKSPACE: No linked workspace found for user');
        return null;
      }
    }
    
    return workspaceId;
  } catch (error) {
    console.error('WORKSPACE ERROR: Failed to fetch workspace_id', error);
    return null;
  }
};

// Export all functions as named exports
export { saveArticleToDirectus, updateArticleStatusToOngoing, getWorkspaceIdHelper as getWorkspaceId };